export interface ManagedArSession {
  start(): Promise<void>;
  stop(): Promise<void>;
  destroy(): void;
}

export type SessionFactory = () => ManagedArSession | Promise<ManagedArSession>;

export type SessionReleaseReason = "manual" | "hidden" | "pagehide" | "beforeunload" | "restart";

export interface SessionLifecycleOptions {
  document?: Document;
  window?: Window;
  cleanupRoot?: ParentNode;
  onRestartRequired?: () => void;
  onReleased?: (reason: SessionReleaseReason) => void;
}

export class SessionLifecycle {
  private readonly documentRef: Document;
  private readonly windowRef: Window;
  private readonly cleanupRoot: ParentNode;
  private currentSession?: ManagedArSession;
  private startPromise?: Promise<void>;
  private restartRequired = false;
  private attached = false;

  constructor(
    private readonly createSession: SessionFactory,
    private readonly options: SessionLifecycleOptions = {},
  ) {
    this.documentRef = options.document ?? document;
    this.windowRef = options.window ?? window;
    this.cleanupRoot = options.cleanupRoot ?? this.documentRef;
    this.attachPageEvents();
  }

  async start(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    if (this.currentSession) {
      return;
    }

    this.restartRequired = false;
    this.startPromise = this.createAndStartSession();

    try {
      await this.startPromise;
    } finally {
      this.startPromise = undefined;
    }
  }

  async release(reason: SessionReleaseReason = "manual"): Promise<void> {
    const session = this.currentSession;
    this.currentSession = undefined;

    if (reason !== "manual" && reason !== "restart") {
      this.restartRequired = true;
    }

    if (session) {
      try {
        await session.stop();
      } finally {
        session.destroy();
      }
    }

    stopMediaTracksIn(this.cleanupRoot);
    cleanupArCanvases(this.cleanupRoot);
    this.options.onReleased?.(reason);
  }

  async destroy(): Promise<void> {
    this.detachPageEvents();
    await this.release("manual");
  }

  get hasActiveSession(): boolean {
    return Boolean(this.currentSession);
  }

  get requiresUserRestart(): boolean {
    return this.restartRequired;
  }

  private async createAndStartSession(): Promise<void> {
    const session = await this.createSession();
    this.currentSession = session;

    try {
      await session.start();
    } catch (error) {
      this.currentSession = undefined;
      session.destroy();
      stopMediaTracksIn(this.cleanupRoot);
      cleanupArCanvases(this.cleanupRoot);
      throw error;
    }
  }

  private attachPageEvents(): void {
    if (this.attached) {
      return;
    }

    this.documentRef.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.windowRef.addEventListener("pagehide", this.handlePageHide);
    this.windowRef.addEventListener("pageshow", this.handlePageShow);
    this.windowRef.addEventListener("beforeunload", this.handleBeforeUnload);
    this.attached = true;
  }

  private detachPageEvents(): void {
    if (!this.attached) {
      return;
    }

    this.documentRef.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.windowRef.removeEventListener("pagehide", this.handlePageHide);
    this.windowRef.removeEventListener("pageshow", this.handlePageShow);
    this.windowRef.removeEventListener("beforeunload", this.handleBeforeUnload);
    this.attached = false;
  }

  private readonly handleVisibilityChange = (): void => {
    if (this.documentRef.visibilityState === "hidden") {
      void this.release("hidden");
    }
  };

  private readonly handlePageHide = (): void => {
    void this.release("pagehide");
  };

  private readonly handlePageShow = (): void => {
    if (this.restartRequired) {
      this.options.onRestartRequired?.();
    }
  };

  private readonly handleBeforeUnload = (): void => {
    void this.release("beforeunload");
  };
}

export function stopMediaTracksIn(root: ParentNode): number {
  let stoppedTracks = 0;
  const videos = Array.from(root.querySelectorAll("video"));

  for (const video of videos) {
    const stream = video.srcObject;

    if (hasTracks(stream)) {
      for (const track of stream.getTracks()) {
        track.stop();
        stoppedTracks += 1;
      }
    }

    video.pause();
    video.srcObject = null;
    video.removeAttribute("src");
    video.load();
  }

  return stoppedTracks;
}

export function cleanupArCanvases(root: ParentNode): void {
  const canvases = Array.from(root.querySelectorAll("canvas"));

  for (const canvas of canvases) {
    canvas.remove();
  }
}

function hasTracks(value: unknown): value is { getTracks(): Array<{ stop(): void }> } {
  return typeof value === "object" && value !== null && "getTracks" in value;
}
